import { SelectOptions } from 'components/commonType';
import { ADDONFEATURE } from 'core/agency/AddonFeature';
import { AddonFeatureManager } from 'core/auth/AddonFeatureManager';
import { Permission } from 'core/auth/Permission';
import { BidStrategy } from 'core/l2Object/L2Object';
import { PermissionItem } from 'core/permission/PermissionAware';
import { addOnEnabled, hasFuncs } from 'core/permission/PermissionDSL';
import { RtbObjective } from 'core/rtbCampaignGroup/RtbCampaignGroup';
import { createSelectOptionsFromEnum } from 'utils/SelectOptionsUtils';
import { L1ObjectCreateRequest, L1ObjectUpdateRequest, L1ObjectWebService, RestfulL1ObjectWebService } from 'ws/L1ObjectWebService';
import { L1ObjectChannel, L1ObjectObjective } from './L1Object';
import { get } from 'lodash';

export interface L1ObjectManager {
  createL1Object (l1Object: L1ObjectCreateRequest): Promise<number>;
  updateL1Object (l1ObjectId: number | string, l1Object: L1ObjectUpdateRequest): Promise<void>;
  getL1Object (l1ObjectId: number | string): Promise<any>;
  getL1Objects (orderId: number | string): Promise<Array<any>>;
  deleteL1Object (l1ObjectId: number): Promise<void>;
  updateL1ObjectState (l1ObjectIds: (number | string)[], state: 'activate' | 'deactivate'): Promise<void>;
  getL1ObjectName (l1ObjectId: number): Promise<string | null>;
  getOrderNumber (l1ObjectId: string | number): Promise<string>;
  getL1ObjectChannelOptions (): SelectOptions[];
  getL1ObjectObjectiveOptions (channel: L1ObjectChannel): SelectOptions[];
  getL1ObjectBidStrategyOptions (): SelectOptions[];
  getNativeObjectiveValue (channel: L1ObjectChannel, l1ObjectObjective?: string): RtbObjective | undefined;
  getAllowedChannels (addonFeatureManager: AddonFeatureManager): string[];
  isChannelAllowed (channel: L1ObjectChannel, addonFeatureManager: AddonFeatureManager): boolean;
  getModifyPermissionItem (l1Object?: {
    channel: string
  }): PermissionItem;
}

export class DefaultL1ObjectManager implements L1ObjectManager {
  webService: L1ObjectWebService;

  constructor (
    webService: L1ObjectWebService = new RestfulL1ObjectWebService()
  ) {
    this.webService = webService;
  }

  async createL1Object (l1Object: L1ObjectCreateRequest): Promise<number> {
    return this.webService.createL1Object(l1Object);
  }
  async updateL1Object (l1ObjectId: number | string, l1Object: L1ObjectUpdateRequest): Promise<void> {
    return this.webService.updateL1Object(l1ObjectId, l1Object);
  }
  async getL1Object (l1ObjectId: number | string): Promise<any> {
    return this.webService.getL1Object(l1ObjectId);
  }
  async getL1Objects (orderId: number | string): Promise<any> {
    return this.webService.getL1Objects(orderId);
  }
  async deleteL1Object (l1ObjectId: number): Promise<void> {
    return this.webService.deleteL1Object(l1ObjectId);
  }
  async updateL1ObjectState (l1ObjectIds: (number | string)[], state: 'activate' | 'deactivate'): Promise<void> {
    await this.webService.updateL1ObjectState(l1ObjectIds, state);
  }
  async getL1ObjectName (l1ObjectId: number): Promise<string | null> {
    return this.webService.getL1ObjectName(l1ObjectId);
  }
  async getOrderNumber (l1ObjectId: string | number): Promise<string> {
    return this.webService.getOrderNumber(l1ObjectId);
  }

  getL1ObjectChannelOptions (): SelectOptions[] {
    return createSelectOptionsFromEnum(L1ObjectChannel, 'l1Object.labels.channel_');
  }

  getL1ObjectObjectiveOptions (channel: L1ObjectChannel): SelectOptions[] {
    const allowedObjectiveMap = {
      [L1ObjectChannel.RTB]: [L1ObjectObjective.AWARENESS, L1ObjectObjective.TRAFFIC]
    };
    const ignoreObjectives = Object.values(L1ObjectObjective).filter(objective => !allowedObjectiveMap[channel].includes(objective));
    return createSelectOptionsFromEnum(L1ObjectObjective, 'l1Object.labels.objective.', [...ignoreObjectives]);
  }

  getL1ObjectBidStrategyOptions (): SelectOptions[] {
    return createSelectOptionsFromEnum(BidStrategy, 'l1Object.labels.bidStrategy.');
  }

  getNativeObjectiveValue (channel: L1ObjectChannel, l1ObjectObjective?: string) {
    if (!l1ObjectObjective) {
      return;
    }
    const nativeObjectiveMap = {
      [L1ObjectChannel.RTB]: {
        [L1ObjectObjective.AWARENESS]: RtbObjective.AWARENESS,
        [L1ObjectObjective.TRAFFIC]: RtbObjective.TRAFFIC
      }
    };
    const channelMap = nativeObjectiveMap[channel];
    return channelMap[l1ObjectObjective];
  }

  getAllowedChannels (addonFeatureManager: AddonFeatureManager): string[] {
    return Object.keys(L1ObjectChannel)
      .filter((channel: string) => {
        return this.isChannelAllowed(L1ObjectChannel[channel], addonFeatureManager);
      });
  }

  isChannelAllowed (channel: L1ObjectChannel, addonFeatureManager: AddonFeatureManager): boolean {
    const featureName = ADDONFEATURE.CHANNEL[channel];
    if (!featureName) {
      return true;
    }
    return addonFeatureManager.isFeatureEnable(featureName);
  }

  getModifyPermissionItem (l1Object?: {
    channel: string
  }): PermissionItem {
    const channel = get(l1Object, 'channel');
    let basicPermissionAware = hasFuncs(Permission.CAMPAIGN_WRITE);
    if (channel) {
      const channelFeatureName = ADDONFEATURE.CHANNEL[channel];
      if (channelFeatureName) {
        basicPermissionAware = basicPermissionAware.and(addOnEnabled(channelFeatureName));
      }
    }

    return basicPermissionAware;
  }
}
